import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import AccountsUsersService, { downloadBlob } from '../../../services/AccountsUsersService';
import { Loading } from '../../Loading';
import Paginator from '../../Paginator';
import ConfirmationModal from '../../ConfirmationModal';
import { UsersTable } from './UsersTable';
import { UsersFilters } from './UsersFilters';
import { UserModal } from './UserModal';
import usePermissions from '../../../hooks/usePermissions';

const EMPTY_FILTERS = { search: '', role: '' };

export function UsersTab() {
    const { can } = usePermissions();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [drafts, setDrafts] = useState(EMPTY_FILTERS);

    // Modals
    const [userModal, setUserModal] = useState({ open: false, isNew: true, user: null });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [primaryTarget, setPrimaryTarget] = useState(null);
    const [notifyTarget, setNotifyTarget] = useState(null);
    const [activeTarget, setActiveTarget] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await AccountsUsersService.getUsers({ page, per_page: perPage, ...filters });
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, filters]);

    useEffect(() => {
        fetchUsers();
        return () => { setItems([]); };
    }, [fetchUsers, refreshKey]);

    const handleFilterChange = (name, value) => {
        setDrafts((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = () => {
        setFilters({ ...drafts });
        setPage(1);
        setRefreshKey((k) => k + 1);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setDrafts(EMPTY_FILTERS);
        setPage(1);
        setRefreshKey((k) => k + 1);
    };

    const flash = (msg) => {
        setFeedback(msg);
        setTimeout(() => setFeedback(''), 4000);
    };

    // Create / edit
    const openCreate = () => setUserModal({ open: true, isNew: true, user: null });
    const openEdit = (u) => setUserModal({ open: true, isNew: false, user: u });

    const handleSaveUser = async (payload) => {
        setSaving(true);
        try {
            if (userModal.isNew) {
                await AccountsUsersService.createUser(payload);
                flash('Usuario creado correctamente');
            } else {
                await AccountsUsersService.updateUser({ ...payload, id: userModal.user.id });
                flash('Usuario actualizado correctamente');
            }
            setUserModal({ open: false, isNew: true, user: null });
            setRefreshKey((k) => k + 1);
            return { ok: true };
        } catch (e) {
            return { ok: false, msg: e.message };
        } finally {
            setSaving(false);
        }
    };

    // Assign primary user
    const handleConfirmPrimary = async () => {
        setSaving(true);
        try {
            await AccountsUsersService.assignPrimaryUser(primaryTarget.id);
            setPrimaryTarget(null);
            flash('Usuario asignado como principal de la cuenta');
            setRefreshKey((k) => k + 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // Notify account ready
    const handleConfirmNotify = async () => {
        setSaving(true);
        try {
            await AccountsUsersService.notifyUser(notifyTarget.id);
            setNotifyTarget(null);
            flash('Correo de bienvenida enviado correctamente');
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // Delete
    const handleConfirmDelete = async () => {
        setSaving(true);
        try {
            await AccountsUsersService.deleteUser(deleteTarget.id);
            setDeleteTarget(null);
            flash('Usuario eliminado correctamente');
            setRefreshKey((k) => k + 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // Activate / deactivate
    const handleConfirmToggleActive = async () => {
        if (!activeTarget) return;
        const nextActive = activeTarget.active === 1 ? 0 : 1;
        setSaving(true);
        try {
            await AccountsUsersService.setUserActive(activeTarget.id, nextActive);
            setActiveTarget(null);
            flash(nextActive === 1
                ? `Usuario ${activeTarget.user} reactivado correctamente`
                : `Usuario ${activeTarget.user} desactivado correctamente`);
            setRefreshKey((k) => k + 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // Export (respects applied filters)
    const handleExport = async () => {
        try {
            const blob = await AccountsUsersService.exportUsers(filters);
            downloadBlob(blob, `usuarios_${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (e) {
            setError(e.message);
        }
    };

    return (
        <>
            {error && (
                <div className="alert alert-danger py-2">{error}
                    <button className="btn-close ms-2 float-end" onClick={() => setError('')} />
                </div>
            )}
            {feedback && (
                <div className="alert alert-success py-2">{feedback}
                    <button className="btn-close ms-2 float-end" onClick={() => setFeedback('')} />
                </div>
            )}

            <UsersFilters
                filters={drafts}
                onChange={handleFilterChange}
                onSearch={handleSearch}
                onReset={handleReset}
            />

            <div className="row mb-3 mt-4">
                <div className="col-md-12 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-uppercase text-white opacity-75">{total} usuario(s)</span>
                    <div className="d-flex gap-2">
                        {/* El boton de exportar pide 'users:view' en el backend, no un
                            permiso propio: es la misma lectura de la lista, en CSV. */}
                        {can('users', 'view') && (
                            <button className="btn btn-outline-secondary border-secondary text-white-50 px-3" onClick={handleExport} title="Exportar CSV">
                                <FontAwesomeIcon icon={faFileCsv} /> Exportar
                            </button>
                        )}
                        <button className="btn btn-ink-icon" onClick={() => setRefreshKey(k => k + 1)} title="Recargar lista">
                            <FontAwesomeIcon icon={faSync} spin={loading} />
                        </button>
                        {can('users', 'create') && (
                            <button className="btn btn-success px-3" onClick={openCreate} title="Agregar usuario">
                                <FontAwesomeIcon icon={faPlus} className="me-1" /> Agregar
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {loading ? <Loading /> : (
                <>
                    <UsersTable
                        users={items}
                        onEdit={openEdit}
                        onNotify={(u) => setNotifyTarget(u)}
                        onDelete={(u) => setDeleteTarget(u)}
                        onAssignPrimary={(u) => setPrimaryTarget(u)}
                        onToggleActive={(u) => setActiveTarget(u)}
                    />
                    {total > perPage && (
                        <Paginator totalItems={total} itemsPerPage={perPage} currentPage={page} onPageChange={setPage} />
                    )}
                </>
            )}

            <UserModal
                isOpen={userModal.open}
                onClose={() => setUserModal({ open: false, isNew: true, user: null })}
                initial={userModal.user}
                isNew={userModal.isNew}
                onSave={handleSaveUser}
                saving={saving}
            />

            <ConfirmationModal
                isOpen={primaryTarget !== null}
                onClose={() => setPrimaryTarget(null)}
                onConfirm={handleConfirmPrimary}
                title="Asignar usuario principal"
                message={`Se marcará a "${primaryTarget ? primaryTarget.name : ''}" como usuario principal de su cuenta. El usuario principal anterior quedará como un usuario regular (ningún usuario se desactiva).`}
                confirmText="Asignar"
                type="question"
                isLoading={saving}
            />

            <ConfirmationModal
                isOpen={notifyTarget !== null}
                onClose={() => setNotifyTarget(null)}
                onConfirm={handleConfirmNotify}
                title="Notificar cuenta lista"
                message={`Se enviará un correo de bienvenida a "${notifyTarget ? notifyTarget.email : ''}" confirmando que su cuenta de usuario está lista.`}
                confirmText="Enviar correo"
                type="question"
                isLoading={saving}
            />

            <ConfirmationModal
                isOpen={activeTarget !== null}
                onClose={() => setActiveTarget(null)}
                onConfirm={handleConfirmToggleActive}
                title={activeTarget && activeTarget.active === 1 ? 'Desactivar usuario' : 'Reactivar usuario'}
                message={activeTarget && activeTarget.active === 1
                    ? `¿Desactivar al usuario "${activeTarget.name || activeTarget.user}" (${activeTarget.user})? Dejará de poder iniciar sesión de inmediato, y podrás reactivarlo desde aquí cuando quieras.`
                    : `¿Reactivar al usuario "${activeTarget ? (activeTarget.name || activeTarget.user) : ''}" (${activeTarget ? activeTarget.user : ''})? Volverá a poder iniciar sesión en su cuenta.`}
                confirmText={activeTarget && activeTarget.active === 1 ? 'Desactivar' : 'Reactivar'}
                type="warning"
                isLoading={saving}
            />

            <ConfirmationModal
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title="Eliminar usuario"
                message={`¿Estás seguro de eliminar al usuario "${deleteTarget ? deleteTarget.name : ''}" (${deleteTarget ? deleteTarget.user : ''})? Ya no podrá iniciar sesión.`}
                confirmText="Eliminar"
                type="danger"
                isLoading={saving}
            />
        </>
    );
}

export default UsersTab;